import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import OverrideModal from './OverrideModal';

// Badge text colours are intentionally fixed: they sit on saturated accent
// backgrounds that keep their hue in dark mode.
const STATUS_BADGE_STYLES = {
  approved: { backgroundColor: 'var(--color-success)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8em' },
  rejected: { backgroundColor: 'var(--color-danger-dk)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8em' },
  pending: { backgroundColor: 'var(--color-warning)', color: '#000', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8em' },
};

const getApprovalStatus = (app) => {
  if (!app.ManagerApproval || app.ManagerApproval.approved === null || app.ManagerApproval.approved === undefined) {
    return 'pending';
  }
  return app.ManagerApproval.approved ? 'approved' : 'rejected';
};

const getStatusBadge = (status) => {
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return <span style={STATUS_BADGE_STYLES[status]}>{label}</span>;
};

const daysSince = (app) => {
  if (!app.submitted_at) return '—';
  const status = getApprovalStatus(app);
  if (status === 'approved' || status === 'rejected') {
    // Show days between submission and approval
    if (app.ManagerApproval?.approved_at) {
      return Math.floor((new Date(app.ManagerApproval.approved_at).getTime() - new Date(app.submitted_at).getTime()) / (1000 * 60 * 60 * 24));
    }
    return '—';
  }
  return Math.floor((Date.now() - new Date(app.submitted_at).getTime()) / (1000 * 60 * 60 * 24));
};

const ApprovalsTab = ({ currentUser, configVersion }) => {
  const [applications, setApplications] = useState([]);
  const [typeConfig, setTypeConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterChapter, setFilterChapter] = useState(
    currentUser?.chapter_id ? String(currentUser.chapter_id) : 'all'
  );
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterName, setFilterName] = useState('');
  const [overrideApp, setOverrideApp] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [history, setHistory] = useState({});
  const [expanded, setExpanded] = useState({});

  const isAdmin = currentUser?.Role?.permission_level >= 100;
  const userChapterId = currentUser?.chapter_id;
  const canEditChapter = (chapterId) => isAdmin || chapterId === userChapterId;
  const [reminding, setReminding] = useState({});
  const [remindingAll, setRemindingAll] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkListExpanded, setBulkListExpanded] = useState(false);

  const fetchData = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const [appsRes, typeRes, chaptersRes] = await Promise.all([
        axios.get(`/api/applications/submissions?year=${currentYear}`),
        axios.get('/api/manager_approval_type_config'),
        axios.get('/api/chapters'),
      ]);
      setApplications(appsRes.data || []);
      setTypeConfig(typeRes.data || []);
      setChapters(chaptersRes.data || []);
      // History only drives the row expander, so release the table as soon as the primary
      // data lands rather than holding the whole pane behind a secondary fetch.
      setLoading(false);

      // One batched request. This used to be one request per application with a
      // ManagerApproval record, which the browser throttled to ~6 concurrent.
      const appsWithApproval = (appsRes.data || []).filter(a => a.ManagerApproval);
      if (appsWithApproval.length === 0) {
        setHistory({});
        return;
      }
      try {
        // Chunked so the querystring cannot outgrow a proxy's URL limit on a large cohort.
        const CHUNK = 250;
        const merged = {};
        for (let i = 0; i < appsWithApproval.length; i += CHUNK) {
          const ids = appsWithApproval.slice(i, i + CHUNK).map(a => a.id).join(',');
          const historyRes = await axios.get(`/api/applications/approve/history?ids=${ids}`);
          Object.assign(merged, historyRes.data || {});
        }
        setHistory(merged);
      } catch (historyErr) {
        console.error('Error fetching approval history:', historyErr);
        setHistory({});
      }
    } catch (err) {
      console.error('Error fetching approvals data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [configVersion]);

  // Socket subscriptions
  useEffect(() => {
    const socket = io({ path: '/socket.io/', transports: ['websocket', 'polling'], withCredentials: true });

    socket.on('manager-approval:updated', () => {
      fetchData();
    });

    socket.on('manager-approval-config:updated', () => {
      fetchData();
    });

    return () => {
      socket.off('manager-approval:updated');
      socket.off('manager-approval-config:updated');
      socket.disconnect();
    };
  }, []);

  // Filter logic: only show required types per chapter
  const isRequired = (app) => {
    const chapterConfig = typeConfig.find(c => c.chapter_id === app.chapter_id);
    // Default: only mentee required when no config row exists for this chapter
    const config = chapterConfig || { mentee_required: true, mentor_required: false, tcm_required: false };
    const appType = (app.ApplicantType?.applicant_type || '').toLowerCase();
    if (appType === 'mentee') return config.mentee_required;
    if (appType === 'mentor') return config.mentor_required;
    if (appType === 'commitee' || appType === 'committee') return config.tcm_required;
    return true;
  };

  // Show Role Type filter only if any chapter has mentor or committee approvals enabled
  const showRoleFilter = typeConfig.some(c => c.mentor_required || c.tcm_required);
  const showMentorOption = typeConfig.some(c => c.mentor_required);
  const showCommitteeOption = typeConfig.some(c => c.tcm_required);

  const filteredApps = applications
    .filter(app => app.status_id !== 1 && app.status_id !== 3) // Exclude Pending (draft) and Withdrawn
    .filter(isRequired)
    .filter(app => filterChapter === 'all' || app.chapter_id === Number(filterChapter))
    .filter(app => {
      if (!filterName.trim()) return true;
      const q = filterName.trim().toLowerCase();
      return (app.User?.name || '').toLowerCase().includes(q)
        || (app.manager || '').toLowerCase().includes(q);
    })
    .filter(app => {
      if (filterStatus === 'all') return true;
      return getApprovalStatus(app) === filterStatus;
    })
    .filter(app => {
      if (filterRole === 'all') return true;
      const type = (app.ApplicantType?.applicant_type || '').toLowerCase();
      return type === filterRole || (filterRole === 'committee' && type === 'commitee');
    })
    .sort((a, b) => {
      // Pending first, then by days pending descending
      const statusA = getApprovalStatus(a);
      const statusB = getApprovalStatus(b);
      if (statusA === 'pending' && statusB !== 'pending') return -1;
      if (statusB === 'pending' && statusA !== 'pending') return 1;
      return (new Date(a.submitted_at) - new Date(b.submitted_at));
    });

  const pendingApps = filteredApps.filter(app => getApprovalStatus(app) === 'pending');
  const editablePendingApps = pendingApps.filter(app => canEditChapter(app.chapter_id));

  const sendReminder = async (appId) => {
    setReminding(prev => ({ ...prev, [appId]: true }));
    try {
      await axios.post(`/api/applications/${appId}/remind-manager`);
    } catch (err) {
      console.error('Failed to send reminder:', err);
      alert(err.response?.data?.error || 'Failed to send reminder');
    } finally {
      setReminding(prev => ({ ...prev, [appId]: false }));
    }
  };

  const sendAllReminders = async () => {
    if (!editablePendingApps.length) return;
    setShowBulkModal(false);
    setRemindingAll(true);
    let sent = 0;
    let failed = 0;
    for (const app of editablePendingApps) {
      try {
        await axios.post(`/api/applications/${app.id}/remind-manager`);
        sent++;
      } catch {
        failed++;
      }
    }
    setRemindingAll(false);
    alert(`Reminders sent: ${sent}${failed ? `, failed: ${failed}` : ''}`);
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div>
      {/* Filter controls */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
        <label>
          Search:{' '}
          <input
            type="text"
            placeholder="Name or manager email..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ marginLeft: '4px', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--color-border-input)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', minWidth: '180px' }}
          />
        </label>
        <label>
          Chapter:{' '}
          <select value={filterChapter} onChange={(e) => setFilterChapter(e.target.value)} disabled={!isAdmin} title={isAdmin ? undefined : 'Officers see their own chapter only'} aria-label="Chapter" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border-input)', borderRadius: '4px', padding: '4px 8px' }}>
            <option value="all">All</option>
            {chapters.map(ch => (
              <option key={ch.id} value={ch.id}>{ch.location}</option>
            ))}
          </select>
        </label>
        {showRoleFilter && (
          <label>
            Role Type:{' '}
            <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border-input)', borderRadius: '4px', padding: '4px 8px' }}>
              <option value="all">All</option>
              <option value="mentee">Mentee</option>
              {showMentorOption && <option value="mentor">Mentor</option>}
              {showCommitteeOption && <option value="committee">Committee</option>}
            </select>
          </label>
        )}
        <label>
          Approval Status:{' '}
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border-input)', borderRadius: '4px', padding: '4px 8px' }}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </label>
        {editablePendingApps.length > 0 && (
          <button
            onClick={() => { setShowBulkModal(true); setBulkListExpanded(false); }}
            disabled={remindingAll}
            title="You will be asked to confirm before emails are sent"
            style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: '4px', border: '1px solid var(--color-border-input)', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer', fontSize: '0.85em', whiteSpace: 'nowrap' }}
          >
            {remindingAll ? 'Sending...' : `✉ Send All Reminders (${editablePendingApps.length})`}
          </button>
        )}
        <span style={{ marginLeft: editablePendingApps.length > 0 ? '16px' : 'auto', fontSize: '0.85em', color: 'var(--color-text-faded)', whiteSpace: 'nowrap' }}>
          Showing {filteredApps.length} {filteredApps.length === 1 ? 'application' : 'applications'}
        </span>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
              <th style={{ padding: '8px' }}>Applicant</th>
              <th style={{ padding: '8px' }}>Chapter</th>
              <th style={{ padding: '8px' }}>Role Type</th>
              <th style={{ padding: '8px' }}>Submitted</th>
              <th style={{ padding: '8px' }}>Days Pending</th>
              <th style={{ padding: '8px' }}>Manager Email</th>
              <th style={{ padding: '8px' }}>Approval Status</th>
              <th style={{ padding: '8px' }}>Comments</th>
              <th style={{ padding: '8px' }}>Override</th>
              <th style={{ padding: '8px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApps.map((app) => {
              const status = getApprovalStatus(app);
              const appHistory = history[app.id] || [];
              const isExpanded = expanded[app.id];
              return (
                <React.Fragment key={app.id}>
                <tr style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--color-border)' }}>
                  <td style={{ padding: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {appHistory.length > 0 && (
                        <button
                          onClick={() => setExpanded(prev => ({ ...prev, [app.id]: !prev[app.id] }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', fontSize: '0.75em', color: 'var(--color-text-faded)', display: 'flex', alignItems: 'center', outline: 'none' }}
                          title="Toggle history"
                        >
                          <span style={{ display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>&#9654;</span>
                        </button>
                      )}
                      {app.User?.name || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '8px' }}>{app.Chapter?.location || '—'}</td>
                  <td style={{ padding: '8px' }}>{app.ApplicantType?.applicant_type || '—'}</td>
                  <td style={{ padding: '8px' }}>{app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '—'}</td>
                  <td style={{ padding: '8px' }}>{daysSince(app)}</td>
                  <td style={{ padding: '8px', fontSize: '0.85em' }}>{app.manager || '—'}</td>
                  <td style={{ padding: '8px' }}>{getStatusBadge(status)}</td>
                  <td style={{ padding: '8px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: app.ManagerApproval?.comments ? 'help' : 'default' }} title={app.ManagerApproval?.comments || ''}>
                    {app.ManagerApproval?.comments || '—'}
                  </td>
                  <td style={{ padding: '8px' }}>
                    {app.ManagerApproval?.is_override && (
                      <span style={{ backgroundColor: 'var(--color-surface-alt)', color: 'var(--color-primary)', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8em', border: '1px solid var(--color-border)' }}>Override</span>
                    )}
                  </td>
                  <td style={{ padding: '8px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => setOverrideApp(app)}
                        disabled={!canEditChapter(app.chapter_id)}
                        {...(!canEditChapter(app.chapter_id) ? { title: 'You can only override approvals for your chapter' } : {})}
                        style={{ padding: '4px 10px', fontSize: '0.85em', borderRadius: '4px', border: '1px solid var(--color-border-input)', background: 'var(--color-surface)', color: !canEditChapter(app.chapter_id) ? 'var(--color-disabled)' : 'var(--color-text)', cursor: canEditChapter(app.chapter_id) ? 'pointer' : 'not-allowed', opacity: canEditChapter(app.chapter_id) ? 1 : 0.5 }}
                      >
                        Override
                      </button>
                      {status === 'pending' && (
                        <button
                          onClick={() => sendReminder(app.id)}
                          disabled={reminding[app.id] || !canEditChapter(app.chapter_id)}
                          {...(!canEditChapter(app.chapter_id) ? { title: 'You can only send reminders for your chapter' } : { title: "Immediately sends a reminder email to this applicant's manager" })}
                          style={{ padding: '4px 10px', fontSize: '0.85em', borderRadius: '4px', border: '1px solid var(--color-border-input)', background: 'var(--color-surface)', color: !canEditChapter(app.chapter_id) ? 'var(--color-disabled)' : 'var(--color-text)', cursor: canEditChapter(app.chapter_id) ? 'pointer' : 'not-allowed', opacity: canEditChapter(app.chapter_id) ? 1 : 0.5 }}
                        >
                          {reminding[app.id] ? 'Sending...' : '✉ Remind'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {isExpanded && appHistory.length > 0 && (
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td colSpan={10} style={{ padding: '4px 8px 12px 28px', backgroundColor: 'var(--color-surface-alt)' }}>
                      <div style={{ fontSize: '0.84em', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <strong style={{ fontSize: '0.9em', color: 'var(--color-text-faded)' }}>Approval History ({appHistory.length})</strong>
                        {[...appHistory].reverse().map((entry, i) => {
                          let newVal = {};
                          let prevVal = {};
                          try { newVal = JSON.parse(entry.newValue || '{}'); } catch { /* ignore */ }
                          try { prevVal = JSON.parse(entry.previousValue || '{}'); } catch { /* ignore */ }
                          const action = newVal.approved === true ? 'Approved' : newVal.approved === false ? 'Rejected' : 'Updated';
                          const isOverride = newVal.is_override;
                          const who = entry.sourceUser?.name || 'Unknown';
                          const when = new Date(entry.createdTimestamp).toLocaleString();
                          const comment = newVal.comments || '';
                          const prevAction = prevVal.approved === true ? 'Approved' : prevVal.approved === false ? 'Rejected' : null;
                          return (
                            <div key={entry.id || i} style={{ padding: '6px 10px', backgroundColor: 'var(--color-surface)', borderRadius: '4px', borderLeft: '3px solid var(--color-primary)' }}>
                              <div>
                                <strong>{who}</strong>
                                {isOverride && <span style={{ color: 'var(--color-primary)', marginLeft: '4px' }}>(override)</span>}
                                {' — '}
                                <span style={{ fontWeight: 500 }}>{action}</span>
                                {prevAction && <span style={{ color: 'var(--color-text-faded)' }}> (was: {prevAction})</span>}
                              </div>
                              {comment && <div style={{ color: 'var(--color-text-faded)', marginTop: '2px' }}>"{comment}"</div>}
                              <div style={{ color: 'var(--color-text-faded)', fontSize: '0.9em', marginTop: '2px' }}>{when}</div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            {filteredApps.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-faded)' }}>
                  No applications match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Override Modal */}
      {showBulkModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', borderRadius: '8px', padding: '24px', maxWidth: '500px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginTop: 0 }}>Send All Reminders</h3>
            <p>This will immediately send a reminder email to <strong>{editablePendingApps.length}</strong> manager{editablePendingApps.length !== 1 ? 's' : ''} with pending approvals.</p>
            <div style={{ marginBottom: '16px' }}>
              <button
                onClick={() => setBulkListExpanded(prev => !prev)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', padding: 0, fontSize: '0.9em', outline: 'none' }}
              >
                {bulkListExpanded ? '▾' : '▸'} View recipients ({editablePendingApps.length})
              </button>
              {bulkListExpanded && (
                <ul style={{ maxHeight: '180px', overflowY: 'auto', margin: '8px 0 0 0', padding: '0 0 0 20px', fontSize: '0.85em', color: 'var(--color-text-faded)' }}>
                  {editablePendingApps.map(app => (
                    <li key={app.id}>
                      {app.User?.name || 'Unknown'} → {app.manager || 'No manager'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowBulkModal(false); setBulkListExpanded(false); }}
                style={{ padding: '6px 14px', borderRadius: '4px', border: '1px solid var(--color-border-input)', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={sendAllReminders}
                style={{ padding: '6px 14px', borderRadius: '4px', border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer' }}
              >
                ✉ Send {editablePendingApps.length} Reminder{editablePendingApps.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {overrideApp && (
        <OverrideModal
          application={overrideApp}
          onClose={() => setOverrideApp(null)}
          onSuccess={() => {
            setOverrideApp(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default ApprovalsTab;
